import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/highcharts-3d";

const chart_verticals = [
  {
    title: "Real Estate and Rental and Leasing",
    gdp: 40.26,
  },
  {
    title: "Manufacturing",
    gdp: 29.13,
  },
  {
    title: "Gov/Public Administration",
    gdp: 26.24,
  },
  {
    title: "Professional, Scientific, and Technical Services",
    gdp: 23.81,
  },
  {
    title: "Healthcare and Social Assistance",
    gdp: 22.12,
  },
  {
    title: "Finance and Insurance",
    gdp: 21.64,
  },
  {
    title: "Retail Trade",
    gdp: 18.42,
  },
  {
    title: "Wholesale Trade",
    gdp: 17.07,
  },
  {
    title: "Information",
    gdp: 15.69,
  },
  {
    title: "Construction",
    gdp: 13.12,
  },
  {
    title: "Transportation and Warehousing",
    gdp: 9.7,
  },
  {
    title: "Utilities",
    gdp: 4.37,
  },
  {
    title: "Mining",
    gdp: 3.94,
  },
  {
    title: "Arts, Entertainment, and Recreation",
    gdp: 3.37,
  },
  {
    title: "Others",
    gdp: 35.83,
  },
];

const COLORS = [
  "#fa9a25",
  "#95a5a6",
  "#5d6d7e",
  "#7FB3D5",
  "#EC7063",
  "#fadbd8",
  "#96F7E4",
  "#014CB2",
  "#F4A8FF",
  "#7b241c",
  "#196f3d",
  "#f7dc65",
  "#f97316",
  "#a2d9ce",
  "#935116",
  "#17a589",
];

export default function GDP3DPieChart() {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth || 0);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isCompact = containerWidth > 0 && containerWidth < 760;

  // Calculate total and percent data
  const totalGDP = useMemo(
    () => chart_verticals.reduce((sum, v) => sum + v.gdp, 0),
    []
  );

  const dataWithPercent = useMemo(
    () =>
      chart_verticals.map((v, idx) => ({
        name: v.title,
        y: (v.gdp / totalGDP) * 100,
        dataLabels: {
          enabled: !isCompact || activeIndex === idx,
        },
      })),
    [totalGDP, isCompact, activeIndex]
  );

  const options = {
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      options3d: {
        enabled: true,
        alpha: 45,
        beta: 0,
      },
    },
    title: {
      text: "GDP Share Percentage by Sector (2024)",
      style: { fontSize: "20px", fontWeight: "bold", color: "#111" },
    },
    tooltip: {
      pointFormat: "<b>{point.y:.2f}%</b> of total GDP",
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        depth: 35,
        colors: COLORS,
        dataLabels: {
          enabled: false, // point-level enables labels selectively
          format: "{point.name}: {point.y:.2f}%",
          style: {
            fontSize: "13px",
            fontWeight: "500",
            color: "#000",
          },
          allowOverlap: false,
          distance: isCompact ? 10 : 22,
          connectorPadding: 6,
          connectorWidth: 1,
          softConnector: true,
          crop: false, // keep labels outside instead of clipping
          overflow: "justify",
        },
        point: {
          events: {
            click: function () {
              // Toggle label visibility for the clicked slice in compact mode
              if (isCompact) {
                setActiveIndex((prev) =>
                  prev === this.index ? null : this.index
                );
              }
            },
          },
        },
      },
    },
    series: [
      {
        type: "pie",
        name: "GDP Share (%)",
        data: dataWithPercent,
      },
    ],
    credits: { enabled: false },
  };

  return (
    <div
      ref={containerRef}
    >
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
}
